(() => {
  if (window.customElements.get("charlotte-sticky-atc")) {
    return;
  }

  class CharlotteStickyAtc extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.form = this.querySelector("form");
      this.variantControl = this.querySelector("[name='id']");
      this.quantityInput = this.querySelector("[data-charlotte-sticky-quantity]");
      this.priceElement = this.querySelector("[data-charlotte-sticky-price]");
      this.compareElement = this.querySelector("[data-charlotte-sticky-compare]");
      this.imageElement = this.querySelector("[data-charlotte-sticky-image]");
      this.mediaElement = this.querySelector(".charlotte-sticky-atc__media");
      this.submitButton = this.querySelector("[data-charlotte-sticky-submit]");
      this.variants = this.parseVariants();
      this.variantById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.addLabel = this.dataset.addLabel || "Add to cart";
      this.soldOutLabel = this.dataset.soldOutLabel || "Sold out";
      this.unavailableLabel = this.dataset.unavailableLabel || "Unavailable";
      this.moneyFormat = this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || "{{amount}}";

      this.variantControl?.addEventListener("change", () => {
        this.selectVariant(this.variantControl.value, { updateControl: false });
      }, { signal: this.abortController.signal });

      document.addEventListener("variant:change", (event) => {
        if (event.detail?.formId === this.form?.id) {
          return;
        }

        if (event.detail?.variant?.id) {
          this.selectVariant(event.detail.variant.id);
        } else {
          this.setUnavailable();
        }
      }, { signal: this.abortController.signal });

      this.setupVisibility();
      this.selectVariant(this.variantControl?.value);
      this.hidden = false;
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    parseVariants() {
      const script = this.querySelector("[data-charlotte-sticky-variants]");

      if (!script) {
        return [];
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    setupVisibility() {
      this.updateVisibility = this.updateVisibility.bind(this);
      const appScrollContainer = document.getElementById("main");

      window.addEventListener("scroll", this.updateVisibility, { passive: true, signal: this.abortController.signal });
      window.addEventListener("resize", this.updateVisibility, { signal: this.abortController.signal });

      if (appScrollContainer) {
        appScrollContainer.addEventListener("scroll", this.updateVisibility, { passive: true, signal: this.abortController.signal });
      }

      requestAnimationFrame(this.updateVisibility);
    }

    getViewportHeight() {
      return window.visualViewport ? window.visualViewport.height : window.innerHeight;
    }

    getScrollContainer() {
      const main = document.getElementById("main");

      if (main && document.documentElement.classList.contains("charlotte-product-app-scroll-active")) {
        return main;
      }

      return window;
    }

    getScrollTop() {
      const scrollContainer = this.getScrollContainer();

      if (scrollContainer === window) {
        return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      }

      return scrollContainer.scrollTop;
    }

    getElementBottomInScroll(element) {
      if (!element) {
        return 0;
      }

      const scrollContainer = this.getScrollContainer();
      const elementRect = element.getBoundingClientRect();

      if (scrollContainer === window) {
        return elementRect.bottom + this.getScrollTop();
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      return elementRect.bottom - containerRect.top + scrollContainer.scrollTop;
    }

    getVisibilityThresholds() {
      const viewportHeight = this.getViewportHeight();
      const buyButtons = document.querySelector(".product-info__buy-buttons");
      const baseShowAt = Math.max(420, Math.round(viewportHeight * 0.65));
      const bufferAfterBuyButtons = Math.min(150, Math.round(viewportHeight * 0.15));
      const buyButtonsShowAt = buyButtons ? this.getElementBottomInScroll(buyButtons) + bufferAfterBuyButtons : 0;
      const showAt = Math.max(baseShowAt, buyButtonsShowAt);
      const hideAt = Math.max(0, showAt - Math.max(160, Math.round(viewportHeight * 0.22)));

      return { showAt, hideAt };
    }

    updateVisibility() {
      const scrollTop = this.getScrollTop();
      const thresholds = this.getVisibilityThresholds();
      const currentlyVisible = this.classList.contains("is-visible");
      const shouldShow = currentlyVisible ? scrollTop > thresholds.hideAt : scrollTop > thresholds.showAt;

      this.classList.toggle("is-visible", shouldShow);
      document.documentElement.classList.toggle("cs-sticky-atc-visible", shouldShow);
    }

    selectVariant(variantId, { updateControl = true } = {}) {
      const variant = this.variantById.get(String(variantId));

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (updateControl && this.variantControl) {
        this.variantControl.value = variant.id;
      }

      this.updatePrice(variant);
      this.updateImage(variant);
      this.updateQuantityRules(variant);
      this.updateButton(variant);
    }

    setUnavailable() {
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.textContent = this.unavailableLabel;
      }
    }

    updatePrice(variant) {
      if (this.priceElement) {
        this.priceElement.innerHTML = this.formatMoney(variant.price);
      }

      if (!this.compareElement) {
        return;
      }

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.compareElement.hidden = false;
        this.compareElement.innerHTML = this.formatMoney(variant.compare_at_price);
      } else {
        this.compareElement.hidden = true;
        this.compareElement.textContent = "";
      }
    }

    updateImage(variant) {
      if (!this.imageElement || !this.mediaElement) {
        return;
      }

      const image = variant.image || this.dataset.fallbackImage;

      if (!image) {
        this.mediaElement.hidden = true;
        return;
      }

      this.mediaElement.hidden = false;

      if (this.imageElement.src !== image) {
        this.imageElement.src = image;
        this.imageElement.srcset = "";
      }
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput || !variant.quantity_rule) {
        return;
      }

      const min = Number(variant.quantity_rule.min) || 1;
      const increment = Number(variant.quantity_rule.increment) || 1;
      const max = variant.quantity_rule.max ? Number(variant.quantity_rule.max) : null;

      this.quantityInput.min = min;
      this.quantityInput.step = increment;

      if (max) {
        this.quantityInput.max = max;
      } else {
        this.quantityInput.removeAttribute("max");
      }

      const currentValue = Number(this.quantityInput.value) || min;
      this.quantityInput.value = Math.max(min, max ? Math.min(currentValue, max) : currentValue);
      this.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    updateButton(variant) {
      if (!this.submitButton) {
        return;
      }

      this.submitButton.disabled = !variant.available;
      this.submitButton.textContent = variant.available ? this.addLabel : this.soldOutLabel;
    }

    formatMoney(cents) {
      const format = this.moneyFormat;
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);
      const token = match ? match[1] : "amount";
      const value = this.formatWithDelimiters(cents, token);

      return format.replace(placeholderRegex, value);
    }

    formatWithDelimiters(cents, token) {
      let precision = 2;
      let thousands = ",";
      let decimal = ".";

      if (token === "amount_no_decimals") {
        precision = 0;
      } else if (token === "amount_with_comma_separator") {
        thousands = ".";
        decimal = ",";
      } else if (token === "amount_no_decimals_with_comma_separator") {
        precision = 0;
        thousands = ".";
        decimal = ",";
      } else if (token === "amount_with_apostrophe_separator") {
        thousands = "'";
      }

      const number = (Number(cents) / 100).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? decimal + parts[1] : "";

      return dollars + centsPart;
    }
  }

  window.customElements.define("charlotte-sticky-atc", CharlotteStickyAtc);
})();
